import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";

export default function D20Animation() {
  const mountRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    // Get initial dimensions
    const updateDimensions = () => {
      if (mountRef.current) {
        setDimensions({
          width: window.innerWidth,
          height: window.innerHeight
        });
      }
    };

    // Initialize dimensions
    updateDimensions();

    // Handle window resize
    window.addEventListener("resize", updateDimensions);
    
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  useEffect(() => {
    if (!mountRef.current || dimensions.width === 0) return;

    // Scene setup
    const scene = new THREE.Scene();
    
    // Camera setup - use perspective camera for 3D
    const camera = new THREE.PerspectiveCamera(
      75,
      dimensions.width / dimensions.height,
      0.1,
      1000
    );
    camera.position.z = 5;
    
    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true 
    });
    renderer.setSize(dimensions.width, dimensions.height);
    renderer.setClearColor(0x000000, 0); // Transparent background
    mountRef.current.appendChild(renderer.domElement);
    
    // Create icosahedron (d20)
    const geometry = new THREE.IcosahedronGeometry(1, 0);
    
    // Create wireframe material with glowing effect
    const material = new THREE.MeshBasicMaterial({
      color: 0x00ff9c,
      wireframe: true,
      transparent: true,
      opacity: 0.9
    });
    
    const d20 = new THREE.Mesh(geometry, material);
    scene.add(d20);

    // Add subtle ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);
    
    // Add directional light for better visibility of wireframe
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 3, 5);
    scene.add(directionalLight);
    
    // Initialize position and velocity
    let position = new THREE.Vector3(0, 0, 0);
    let velocity = new THREE.Vector3(
      (Math.random() - 0.5) * 0.05,
      (Math.random() - 0.5) * 0.05,
      0
    );
    
    // Ensure velocity is never zero or too slow
    if (Math.abs(velocity.x) < 0.02) velocity.x = 0.02 * Math.sign(velocity.x);
    if (Math.abs(velocity.y) < 0.02) velocity.y = 0.02 * Math.sign(velocity.y);
    
    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      
      // Rotate the d20
      d20.rotation.x += 0.01;
      d20.rotation.y += 0.015;
      
      // Update position
      position.add(velocity);
      d20.position.copy(position);
      
      // Calculate bounds for collision detection
      // Map to screen coordinates
      const boundsSize = 2.5; // Adjust based on camera z position
      
      // Bounce off edges with slight randomization for natural feeling
      if (Math.abs(position.x) > (dimensions.width / dimensions.height) * boundsSize) {
        velocity.x = -velocity.x * (0.97 + Math.random() * 0.06);
        position.x = Math.sign(position.x) * (dimensions.width / dimensions.height) * boundsSize;
        // Change color on collision
        material.color.setHSL(Math.random(), 0.8, 0.6);
      }
      
      if (Math.abs(position.y) > boundsSize) {
        velocity.y = -velocity.y * (0.97 + Math.random() * 0.06);
        position.y = Math.sign(position.y) * boundsSize;
        // Change color on collision
        material.color.setHSL(Math.random(), 0.8, 0.6);
      }
      
      renderer.render(scene, camera);
    };
    
    animate();
    
    // Clean up
    return () => {
      if (mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
    };
  }, [dimensions]);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      zIndex: -1,
      background: 'linear-gradient(to bottom,rgb(11, 0, 49),rgb(47, 5, 44))'
    }}>
      <div ref={mountRef} />
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '20px',
        color: 'rgba(255,255,255,0.5)',
        fontFamily: 'monospace',
        fontSize: '14px'
      }}>
      </div>
    </div>
  );
} 